import React from 'react'
import { FieldErrors, SubmitHandler, SubmitErrorHandler, useWatch } from 'react-hook-form'
import get from 'lodash/get'
import ControlledTextField from 'transfix-form/components/ControlledTextInput'
import { Button } from 'transfix-ui/components/Button'
import { INLINE_MESSAGE_VARIANTS, InlineMessage } from 'transfix-ui/components/InlineMessage'
import { Box, Grid, Typography } from 'transfix-ui/core'
import { ICarrierApplication } from 'types'
import { IStep1 } from 'apps/fleet-planner/components/activation/types'
import { AgreementTerms } from '../AgreementTerms'
import { TSignBcaAgreementSchema } from './SignBcaAgreementSchema'
import { useSignBcaAgreementForm } from './usesignBcaAgreementForm'

const getErrorMessage = <T extends FieldErrors<TSignBcaAgreementSchema>, K extends keyof T>(
  errors: T,
  keyName: K
): string | undefined => {
  return get(errors, [keyName, 'message'])
}

export interface ISignBcaAgreementFormProps {
  /**
   * @default {}
   */
  defaultValues?: Partial<TSignBcaAgreementSchema>
  /**
   * @default {}
   */
  step1Values?: IStep1
  /**
   * @example (data) => {}
   */
  onSubmitSuccess: SubmitHandler<TSignBcaAgreementSchema>
  /**
   * @example (errors) => {}
   */
  onSubmitError?: SubmitErrorHandler<TSignBcaAgreementSchema>
  isLoading?: boolean
  backHandler: () => void
  entity?: string
  ein?: string
  carrierDetails: ICarrierApplication
  formErrors?: string
  mutationIsLoading?: boolean
}

export const SignBcaAgreementForm = ({
  backHandler,
  carrierDetails,
  defaultValues = {},
  entity = '',
  ein = '',
  onSubmitSuccess,
  onSubmitError = () => {},
  mutationIsLoading,
  isLoading,
  formErrors,
}: ISignBcaAgreementFormProps) => {
  const { control, errors, handleSubmit } = useSignBcaAgreementForm({
    defaultValues,
  })

  const submitHandler = handleSubmit(onSubmitSuccess, onSubmitError)

  const agreement = {
    signature: useWatch<TSignBcaAgreementSchema['signatory']>({
      name: 'signatory',
      control,
      defaultValue: '',
    }),
    entity,
    ein,
  }

  return (
    <form onSubmit={submitHandler} data-test='SignBcaAgreementForm'>
      <Box mb={2.5} mt={1.5}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8} lg={6}>
            <Typography variant='h3'>Read & Sign Agreement</Typography>
          </Grid>
          <Box mt={4}>
            <Grid container alignItems='center' justifyContent='center'>
              <Grid item xs={12}>
                <AgreementTerms agreement={agreement} carrierDetails={carrierDetails} />
              </Grid>
            </Grid>
          </Box>

          {formErrors && (
            <Box mt={2}>
              <InlineMessage type={INLINE_MESSAGE_VARIANTS.ALERT} message={formErrors} />
            </Box>
          )}

          <Grid item xs={12}>
            <Box mt='16px'>
              <ControlledTextField
                name='signatory'
                control={control}
                TextInputProps={{
                  label: 'Digital Signature',
                  helperMessage: 'Use your full legal name',
                  error: !!getErrorMessage(errors, 'signatory'),
                  placeholder: 'Enter your full name',
                  dataTest: 'signature-input',
                  required: true,
                  large: true,
                }}
              />
            </Box>
          </Grid>

          <Box pt={2} width='100%'>
            <Grid container justifyContent='flex-end'>
              <Grid item xs={12} md={3}>
                <Button
                  data-test='go-back-bca-button'
                  color='teritary'
                  fullWidth
                  size='large'
                  onClick={backHandler}>
                  Go back
                </Button>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  disabled={mutationIsLoading || isLoading || formErrors}
                  data-test='submit-bca-button'
                  fullWidth
                  size='large'
                  type='submit'>
                  {isLoading ? 'Loading...' : 'Sign and Continue'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Box>
    </form>
  )
}
